import { UserBranchAccessStatus } from "@/features/auth/model/auth-types"

export enum UserStatus {
  ACTIVE = "ACTIVE",
  INVITED = "INVITED",
  DISABLED = "DISABLED",
}

export enum SystemRole {
  ADMIN = "ADMIN",
  FINANCE = "FINANCE",
  PROGRAMS = "PROGRAMS",
  MEMBERS = "MEMBERS",
}

export enum UserAccessScope {
  BRANCH = "BRANCH",
  PARISH = "PARISH",
  PARISH_AND_BRANCH = "PARISH_AND_BRANCH",
}

export interface User {
  id: string
  memberId: string
  email: string
  firstName?: string
  lastName?: string
  roles: SystemRole[]
  accessScope: UserAccessScope
  managedBranchId?: string | null
  status: UserStatus
  invitedAt?: string
  lastLoginAt?: string
  createdAt: string
  updatedAt: string
}

export interface CreateUserInput {
  memberId: string
  email: string
  firstName?: string
  lastName?: string
  roles: SystemRole[]
  accessScope: UserAccessScope
  managedBranchId?: string | null
}

export interface UpdateUserInput {
  roles: SystemRole[]
  accessScope: UserAccessScope
  managedBranchId?: string | null
  status: UserStatus
}

export interface SendInviteInput {
  userId: string
}

export interface InviteTokenRecord {
  id: string
  token: string
  userId: string
  expiresAt: string
  createdAt: string
}

/**
 * Workspace authority role. Only OWNER governs the workspace; everyone else is a USER.
 * Admin was removed (ADR-0014). Mirrors the shared `WorkspaceMembershipRole`.
 */
export enum WorkspaceUserRole {
  OWNER = "OWNER",
  USER = "USER",
}

export type WorkspaceMemberSummary = {
  id: string
  workspaceId: string
  branchId: string | null
  firstName: string
  lastName: string
  email: string | null
  phone: string | null
  avatarUrl: string | null
  branch: {
    id: string
    workspaceId: string
    name: string
    slug: string
    status: string
    branchType: "NORMAL" | "SYSTEM"
  } | null
}

/** Dashboard branch access rows returned with workspace team listings (not member profile). */
export type WorkspaceUserBranchAccessRow = {
  id: string
  branchId: string
  status: UserBranchAccessStatus
  branch: {
    id: string
    name: string
    slug: string
    branchType: "NORMAL" | "SYSTEM"
    status: string
  } | null
}

/** A custom role assigned to a workspace user, optionally scoped to a branch. */
export type WorkspaceTeamRoleAssignment = {
  id: string
  roleId: string
  roleKey: string
  roleName: string
  branchId: string | null
}

export type WorkspaceTeamMember = {
  id: string
  workspaceId: string
  email: string
  firstName: string
  lastName: string
  /** Account avatar (Clerk-synced); falls back to the linked member profile avatar. */
  avatarUrl: string | null
  workspaceRole: WorkspaceUserRole
  userBranchAccesses: WorkspaceUserBranchAccessRow[]
  /** Custom-role assignments (across branches); filter by branchId for a given branch. */
  roles: WorkspaceTeamRoleAssignment[]
  status: "PENDING" | "ACTIVE" | "SUSPENDED"
  member: WorkspaceMemberSummary | null
  /** When this person last opened the app; null if never, absent on older payloads. */
  lastLoginAt?: string | null
  createdAt: string
  updatedAt: string
}

/** A custom role to grant per branch when an invitation is accepted (branchId null = workspace-scoped). */
export type TeamInviteRoleAssignment = {
  branchId: string | null
  roleKey: string
}

export type TeamInviteDepartmentAssignment = {
  departmentId: string
  roleKeys: string[]
}

export type TeamInviteInput = {
  workspaceId: string
  email: string
  role: WorkspaceUserRole
  roleAssignments?: TeamInviteRoleAssignment[]
  departmentAssignments?: TeamInviteDepartmentAssignment[]
}

export type WorkspaceInvitationStatus = "pending" | "accepted" | "declined" | "revoked" | "expired" | string

export type WorkspaceInvitation = {
  id: string
  email: string
  workspaceId: string | null
  branchId: string | null
  branchName: string | null
  workspaceName: string | null
  roleKey: string | null
  isSystemInvite: boolean
  status: WorkspaceInvitationStatus
  code: string | null
  createdAt: string | null
  expiresAt: string | null
  viewedAt: string | null
  acceptedAt: string | null
  /**
   * Custom roles the invitation grants, per branch (branchId null = workspace-scoped).
   *
   * Usually the whole grant: the invite modal expresses per-branch access as roles and sends the module
   * matrix beside them at NONE, so a preview reading only modules reports "no access" for an invitation
   * that grants plenty. The role NAME is resolved on the client — the API returns the bare key because
   * naming it would cost a lookup per invitation in a list endpoint.
   */
  roleAssignments: InvitationGrantedRoleAssignment[]
  departmentAssignments: TeamInviteDepartmentAssignment[]
}

export type InvitationGrantedRoleAssignment = {
  branchId: string | null
  roleKey: string
}
